import { Injectable, signal, inject } from "@angular/core";
import { DateTime } from "luxon";
import { forkJoin, concatMap, of, map, shareReplay, first } from "rxjs";
import { ASSET_TYPES } from "../../../../../enums/assetTypes.enum";
import { GateSystem, GateSystemDTO } from "../../../../../models/api/asset/gateSystem";
import { Location } from "../../../../../models/api/asset/location";
import { GateFault } from "../../../../../models/api/fault/gateFault";
import { Position } from "../../../../../models/shared/position";
import { AssetService } from "../../../../../services/api/asset/asset.service";
import { EnergyToolExtractionService } from "../../../../../services/state/energy-tool-extraction/energy-tool-extraction.service";
import { GateFaultService } from "../../../../../services/api/gate-fault/gate-fault.service";
import { TelemetryService } from "../../../../../services/api/telemetry/telemetry.service";
import { GateFaultUtilsService } from "../../../../../services/utils/gate-fault-utils/gate-fault-utils.service";
import { RealtimeGateFaultResponse } from "../../../../../models/ws/realtimeGateFault";
import { DOCUMENT } from "@angular/common";
import {
  SeveritySubscriptionHandle,
  SeverityService,
} from "../../../../../services/websocket/severity/severity.service";
import { RealtimeSeverity } from "../../../../../models/ws/realtimeSeverity";
import { GateStatusStateService } from "../../../../../services/state/gate-status/gate-status-state.service";
import {
  GateFaultSubscriptionHandle,
  GateFaultWebsocketService,
} from "../../../../../services/websocket/gate-fault/gate-fault-websocket.service";

@Injectable()
export class GateStatusService {
  private readonly _energyLoss = signal<number | null>(null);
  readonly energyLoss = this._energyLoss.asReadonly();

  private readonly _expectedEnergyLossPerDay = signal<number | null>(null);
  readonly expectedEnergyLossPerDay = this._expectedEnergyLossPerDay.asReadonly();

  private readonly gateStatusState = inject(GateStatusStateService);
  public readonly gateStatus = this.gateStatusState.status;
  public readonly gateStatusTimeout = this.gateStatusState.timeout;

  private readonly _activeGateFaultState = signal<GateFault[] | null>(null);
  readonly activeGateFaultState = this._activeGateFaultState.asReadonly();

  private readonly _allGateFaultState = signal<GateFault[] | null>(null);
  readonly allGateFaultState = this._allGateFaultState.asReadonly();

  private readonly _gateSystemCoordinates = signal<Position | null>(null);
  readonly gateSystemCoordinates = this._gateSystemCoordinates.asReadonly();

  private readonly _gateSystemDetail = signal<GateSystem | null>(null);
  readonly gateSystemDetail = this._gateSystemDetail.asReadonly();

  private telemetryService = inject(TelemetryService);
  private assetService = inject(AssetService);
  private energyExtraction = inject(EnergyToolExtractionService);
  private gateFaultService = inject(GateFaultService);
  private gateFaultUtils = inject(GateFaultUtilsService);
  private document = inject(DOCUMENT);
  private localStorage = this.document.defaultView?.localStorage;
  private severityWebsocket = inject(SeverityService);
  private gateFaultWebsocket = inject(GateFaultWebsocketService);
  private severitySubscription?: SeveritySubscriptionHandle;
  private gateFaultSubscription?: GateFaultSubscriptionHandle;

  constructor() {
    this.assetService
      .getAssets(ASSET_TYPES.CUSTOMER)
      .pipe(first())
      .subscribe({
        next: (customers) => {
          const customerIds = customers.filter((c) => c.id).map((c) => c.id) as string[];
          this.initRealtimeSeverity(customerIds);
        },
      });
  }

  /**
   * Resets all state signals to null.
   * This is used to reset the component's state when the user navigates away.
   */
  resetStates() {
    this._energyLoss.set(null);
    this._expectedEnergyLossPerDay.set(null);
    this._activeGateFaultState.set(null);
    this._allGateFaultState.set(null);
    this._gateSystemDetail.set(null);
    this._gateSystemCoordinates.set(null);
  }

  /**
   * Loads initial state of the Gate Status Detail component.
   * @param gateSystemId - The ID of the Gate System asset.
   */
  loadInitialState(gateSystemId: string) {
    this.resetStates();
    this.gateStatusState.load(gateSystemId);
    const now = DateTime.now().toMillis();
    const startOfDay = DateTime.now().startOf("day").toMillis();

    const gateSystem$ = this.fetchGateSystem(gateSystemId);
    const energyLoss$ = this.fetchGateEnergyLoss(gateSystemId, startOfDay, now);

    const allGateFaults$ = this.fetchAllGateFaults(gateSystemId);
    const activeGateFaults$ = this.fetchActiveGateFaults(gateSystemId);
    const location$ = this.fetchLocation(gateSystemId);

    // Subscription
    forkJoin({
      gateSystem: gateSystem$,
      energyLoss: energyLoss$,
      allGateFaults: allGateFaults$,
      activeGateFaults: activeGateFaults$,
      location: location$,
    }).subscribe({
      next: (res) => {
        const energyLoss = res.energyLoss;
        const gateSystem = res.gateSystem;
        const allGateFaults = res.allGateFaults;
        const location = res.location as Location;
        const activeGateFaults = res.activeGateFaults;

        this._activeGateFaultState.set(activeGateFaults);
        this._allGateFaultState.set(allGateFaults);

        // Data for Total Energy Loss Widget
        this.energyExtraction.extractAndSetTotalEnergyLoss(
          energyLoss,
          "today",
          this._energyLoss.set.bind(this._energyLoss)
        );
        this.energyExtraction.extractAndSetExpectedEnergyLossPerDay(
          gateSystem,
          this._expectedEnergyLossPerDay.set.bind(this._expectedEnergyLossPerDay)
        );

        // Data for Gate System Details Widget
        this._gateSystemDetail.set(gateSystem);
        if (location && location.position && location.position.latitude && location.position.longitude) {
          this._gateSystemCoordinates.set(location.position);
        }

        this.initRealtimeGateFaults(gateSystemId);
      },
    });
  }

  /**
   * Destroys the real-time WebSocket connections.
   * This is used when the user navigates away from the Gate Status Detail page.
   * */
  public destroy() {
    this.resetStates();
    this.severitySubscription?.close();
    this.gateFaultSubscription?.close();
  }

  /**
   * Initializes the real-time WebSocket connection for the gate system's severity.
   * @param customerIds customer IDs to listen for gate system severity
   */
  private initRealtimeSeverity(customerIds: string[]) {
    this.severitySubscription?.close();
    const subscription = this.severityWebsocket.register({ customerIds });
    this.severitySubscription = subscription;
    subscription.messages$.subscribe({
      next: (incomingData: RealtimeSeverity) => {
        if (incomingData.assetType === ASSET_TYPES.GATE_SYSTEM) {
          const gateSystem = this._gateSystemDetail();
          if (gateSystem && gateSystem.id === incomingData.assetId) {
            const gateSystemValues = {
              ...gateSystem.values,
              severity: incomingData.severity,
            } as GateSystemDTO;
            this._gateSystemDetail.set(new GateSystem(gateSystemValues));
          }
        }
      },
      error: () => undefined,
    });
  }

  /**
   * Initializes real-time gate faults WebSocket connection.
   * @param gateSystemId gate system ID to listen for
   */
  private initRealtimeGateFaults(selectedGateSystemId: string) {
    this.gateFaultSubscription?.close();
    const subscription = this.gateFaultWebsocket.register({
      entityType: ASSET_TYPES.GATE_SYSTEM,
      entityId: [selectedGateSystemId],
      lang: this.localStorage?.getItem("lang"),
    });
    this.gateFaultSubscription = subscription;
    subscription.messages$.subscribe({
      next: (incomingData) => {
        if (incomingData.data) {
          if (incomingData.entityId === selectedGateSystemId) {
            this.handleRealtimeGateFault(incomingData);
          }
        }
      },
      error: () => undefined,
    });
  }

  /**
   * Processes a real-time gate fault message and updates the gate fault state.
   * @param message The incoming real-time gate fault message.
   */
  private handleRealtimeGateFault(message: RealtimeGateFaultResponse) {
    this.gateFaultUtils.handleRealtimeGateFault(
      message.data!,
      this._activeGateFaultState.update.bind(this._activeGateFaultState),
      false
    );
    this.gateFaultUtils.handleRealtimeGateFault(
      message.data!,
      this._allGateFaultState.update.bind(this._allGateFaultState),
      true
    );
  }

  /**
   * Fetches the location details associated with a specific gate system.
   * @param gateSystemId - The ID of the gate system whose location details are to be fetched.
   * @returns An observable that emits the location details if available, or null if not.
   */
  private fetchLocation(gateSystemId: string) {
    return this.fetchGateSystem(gateSystemId).pipe(
      concatMap((gateSystem) => {
        if (gateSystem && gateSystem.location && gateSystem.location.id) {
          return this.assetService.getAssetDetails(gateSystem.location.id, ASSET_TYPES.LOCATION);
        } else {
          return of(null);
        }
      })
    );
  }

  /**
   * Fetches the gate faults associated with a specific gate system.
   * @param gateSystemId - The ID of the gate system whose faults are to be fetched.
   * @returns An observable that emits an array of gate faults for the specified gate system.
   */

  private fetchAllGateFaults(gateSystemId: string) {
    return this.gateFaultService.getGateFaults(gateSystemId, ASSET_TYPES.GATE_SYSTEM, false);
  }

  private fetchActiveGateFaults(gateSystemId: string) {
    return this.gateFaultService.getGateFaults(gateSystemId, ASSET_TYPES.GATE_SYSTEM, true);
  }

  /**
   * Fetches a gate system by its ID.
   * @param gateSystemId the ID of the gate system to fetch
   * @returns an observable that emits the gate system once it is available
   */
  private fetchGateSystem(gateSystemId: string) {
    return this.assetService.getAssetDetails(gateSystemId, ASSET_TYPES.GATE_SYSTEM).pipe(
      map((res) => {
        return res as GateSystem;
      }),
      shareReplay(1)
    );
  }

  /**
   * Fetches energy loss data for a gate system between a given time range.
   * @param gateSystemId - The ID of the gate system whose energy loss data is to be fetched.
   * @param start - The start of the time range to fetch energy loss for.
   * @param end - The end of the time range to fetch energy loss for.
   * @returns an observable that emits the energy loss data for the specified gate system
   *          and time range.
   */
  private fetchGateEnergyLoss(gateSystemId: string, start: number, end: number) {
    return this.telemetryService.getTimeseries(gateSystemId, ASSET_TYPES.GATE_SYSTEM, "energy_loss", start, end);
  }
}
